<template>
    <v-dialog v-model="dialog" width="600" :fullscreen="isMobileOrTablet()" persistent>
        <v-card class="rounded-lg" style="display: flex; flex-direction: column; height: 100%;">
            <v-card-title style="background-color: #1a5b0f !important; display: flex; justify-content: space-between; align-items: center;">
                <div>
                    <span class="font-weight-bold text-h6 text-white">{{ dialog_title }}</span>
                    <div class="text-caption text-white">{{ dialog_sub_title }}</div>
                </div>
                <v-btn v-if="loading == false" flat icon color="darkGreen" @click="closeDialog()">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="overflow-y: auto; max-height: calc(100vh - 100px);">
                <v-form ref="form" class="mt-2">
                    <v-row>
                        <v-col cols="12">
                            <div class="font-weight-bold">Note: <span class="font-weight-regular">Before uploading your e-sign, please remove the white background first in this website -> 
                                <a href="https://www.remove.bg" target="_blank" rel="noopener noreferrer">https://www.remove.bg</a></span>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="imgChange == true">
                        <v-col cols="12">
                            <v-file-input
                                v-model="file"
                                :rules="[formRules.required]"
                                accept="image/*"
                                label="E-sign Image"
                                variant="outlined"
                                density="comfortable"
                                color="primary"
                                show-size
                            ></v-file-input>
                        </v-col>
                        <v-col cols="12">
                            <v-img
                            v-if="imagePreview"
                            :src="imagePreview"
                            max-height="200"
                            contain
                        ></v-img>
                        </v-col>
                    </v-row>
                    <v-row v-if="imgChange == false" dense>
                        <v-col cols="12" md="7">
                            <v-img
                            class="mx-auto"
                            rounded="lg"
                            :src="imageFile"
                            lazy-src="../../assets/esign.png"
                            loading>
                            <template v-slot:placeholder>
                                <div class="d-flex align-center justify-center fill-height">
                                    <v-progress-circular
                                    color="grey-lighten-4"
                                    indeterminate
                                    ></v-progress-circular>
                                </div>
                            </template>
                            </v-img>
                        </v-col>
                        <v-col cols="12" md="5">
                            <v-btn @click="imgChange = true" class="mr-1" variant="elevated" elevation="1" color="primary">
                                Change E-sign
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions v-if="imgChange == true" class="pa-3" style="margin-top: auto;">
                <v-spacer></v-spacer>
                <v-btn class="mr-1" :disabled="loading" text="Cancel" variant="outlined" elevation="1" @click="closeDialog()"></v-btn>
                <v-btn class="mr-1" :loading="loading" text="Save" variant="elevated" elevation="1" color="darkGreen" @click="validateForm()"></v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios';
import { defineComponent } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'

export default defineComponent({
     emits: ["refresh"],
     data(){
        return {
            dialog: false,
            dialog_title: null,
            dialog_sub_title: null,
            action: null,
            loading: false,

            id: null,
            user_id: null,
            file: null,

            imgChange: true,
            imagePreview: null,
            imageFile: null,
        }
     },
     watch: {
        file(newFile) {
            if (newFile && newFile instanceof File) {
                this.imagePreview = URL.createObjectURL(newFile);
            } else {
                this.imagePreview = null;
            }
        }
    },
     methods: {
        initImage(){
            this.loading = true
            const token = localStorage.getItem('aums-token')
            axios.get(process.env.VUE_APP_SERVER_URL + '/esign/findByUserId/' + this.$store.state.user.id,
            { headers:
                { 
                    'Authorization': `Bearer ${token}`,
                }
            }
            ).then((res)=>{
            if(res.data){
                this.loading = false
                if(res.data.status == 404){
                    this.initAdd()
                }
                else{
                    this.imageFile = `data:image/png;base64,${res.data.b64_name}`;
                    this.initUpdate()
                }
            }
            }).catch((error)=>{
                if(error.response.status == 401){
                    this.loading = false

                    localStorage.removeItem("vuex");
                    localStorage.removeItem("aums-token");
                    window.location.reload();
                }
                else{
                    console.log(error)
                }
            });
        },
        initAdd(){
            this.dialog = true
            this.dialog_title = 'E-sign'
            this.dialog_sub_title = 'Upload or update esign'
            this.action = 'add'

            this.user_id = this.$store.state.user.id
            this.imgChange = true
        },
        initUpdate(){
            this.dialog = true
            this.dialog_title = 'E-sign'
            this.dialog_sub_title = 'Upload or update esign'
            this.action = 'edit'

            this.user_id = this.$store.state.user.id
            this.imgChange = false
        },
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = () => resolve(reader.result.split(',')[1])
                reader.onerror = error => reject(error)
            })
        },
        async validateForm(){
            const { showSnackbar } = useSnackbar()
            const { valid } = await this.$refs.form.validate()

            if(valid){
                this.loading = true

                let base64File = null

                if (this.file instanceof File) {
                    try {
                        base64File = await this.fileToBase64(this.file)
                    } catch (e) {
                        showSnackbar('Failed to read file.')
                        this.loading = false
                        return
                    }
                }

                let formData = {
                    user_id: this.user_id,
                    b64_name: base64File,
                }

                const token = localStorage.getItem('aums-token')

                if(this.action == 'add')[
                    axios.post(process.env.VUE_APP_SERVER_URL + '/esign', formData,
                    { headers:
                        { 
                            'Authorization': `Bearer ${token}`,
                        }
                    }
                    ).then((res)=>{
                        if(res.data.status == 200){
                            this.loading = false
                            this.$emit('refresh')
                            showSnackbar('E-sign successfully uploaded!')
                            this.$refs.form.reset()
                            this.dialog = false
                        }
                    }).catch((error)=>{
                        if(error.response.status == 401){
                            this.loading = false
                            showSnackbar('Token expired! Logging out..')

                            localStorage.removeItem("vuex");
                            localStorage.removeItem("aums-token");
                            window.location.reload();
                        }
                        else{
                            console.log(error)
                        }
                    })
                ]
                else if(this.action == 'edit'){
                    axios.patch(process.env.VUE_APP_SERVER_URL + '/esign', formData,
                    { headers:
                        { 
                            'Authorization': `Bearer ${token}`,
                        }
                    }
                    ).then((res)=>{
                        if(res.data.status == 200){
                            this.loading = false
                            this.$emit('refresh')
                            showSnackbar('E-sign successfully updated!')
                            this.$refs.form.reset()
                            this.dialog = false
                        }
                    }).catch((error)=>{
                        if(error.response.status == 401){
                            this.loading = false
                            showSnackbar('Token expired! Logging out..')

                            localStorage.removeItem("vuex");
                            localStorage.removeItem("aums-token");
                            window.location.reload();
                        }
                        else{
                            console.log(error)
                        }
                    })
                }
            }
        },
        closeDialog(){
            if (this.imagePreview) {
                URL.revokeObjectURL(this.imagePreview);
                this.imagePreview = null;
            }
            this.$refs.form.reset()
            this.dialog = false
        }
     }
})
</script>

<style scoped>

</style>